/**
 * --------------------------------------------------------------------------
 * Bootstrap (v5.1.3): offcanvas.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

import $ from 'jquery';

import { getElementFromSelector, isDisabled, isVisible /* reflow, */ } from '../../bootstrap/util/index.js';

import EventHandler from '../../bootstrap/dom/event-handler.js';
import ScrollBarHelper from '../../bootstrap/util/scrollbar.js';
import SelectorEngine from '../../bootstrap/dom/selector-engine.js';

import Backdrop from './backdrop.js';
import FocusTrap from './focustrap.js';
import MpBaseComponent from './base-component.js';

import { cssAnimation } from '../../util/animate-css.js';
import { loadPartial } from '../../remote/partial-content.js';
import { getCssSizeValue } from '../../util/dom.js';
import { getOptionsFromDataset } from '../../util/dataset.js';
import { handleRemoteResponse } from '../../remote/remote-response.js';

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'offcanvas';
const DATA_KEY = 'bs.offcanvas';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';
const EVENT_LOAD_DATA_API = `load${EVENT_KEY}${DATA_API_KEY}`;
const ESCAPE_KEY = 'Escape';

export const EVENT_SHOW = `show${EVENT_KEY}`;
export const EVENT_PRE_SHOW = `preShow${EVENT_KEY}`;
export const EVENT_SHOWN = `shown${EVENT_KEY}`;
export const EVENT_CONTENT_LOADED = `contentLoaded${EVENT_KEY}`;
export const EVENT_HIDE = `hide${EVENT_KEY}`;
export const EVENT_HIDDEN = `hidden${EVENT_KEY}`;
export const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;
export const EVENT_KEYDOWN_DISMISS = `keydown.dismiss${EVENT_KEY}`;

export const CLASS_NAME_SHOW = 'show';
export const CLASS_NAME_BACKDROP = 'offcanvas-backdrop';

export const OPEN_SELECTOR = '.offcanvas.show';
export const AUTO_OPEN_SELECTOR = '[data-drawer-show]';
export const SELECTOR_DATA_TOGGLE = '[data-bs-toggle="offcanvas"]';

export const COMPONENT_OVERLAY_CLASS = 'mp-overlay-zone';
export const COMPONENT_IS_VISIBLE_CLASS = 'mp-overlay-visible';
const COMPONENT_CONTENT_CLASS = 'offcanvas-content';
const COMPONENT_HEADER_CLASS = 'offcanvas-header';
const COMPONENT_HEADER_EMPTY_CLASS = 'offcanvas-header-transparent';
const COMPONENT_TITLE_CLASS = 'offcanvas-title';
const COMPONENT_BODY_CLASS = 'offcanvas-body';
const COMPONENT_FOOTER_CLASS = 'offcanvas-footer';
const COMPONENT_INNER_CONTENT_SELECTOR = 'div.' + COMPONENT_CONTENT_CLASS;
const COMPONENT_HEADER_SELECTOR = '.' + COMPONENT_HEADER_CLASS;
const COMPONENT_TITLE_SELECTOR = '.' + COMPONENT_TITLE_CLASS;
const COMPONENT_BODY_SELECTOR = '.' + COMPONENT_BODY_CLASS;
const COMPONENT_FOOTER_SELECTOR = '.' + COMPONENT_FOOTER_CLASS;
const COMPONENT_INSTANCE_ATTRIBUTE = 'data-mp-drawer-instance';
// const COMPONENT_INSTANCE_SELECTOR = `[${COMPONENT_INSTANCE_ATTRIBUTE}]`;
const COMPONENT_RELOAD_EVENT = 'mp-section-reload';
const COMPONENT_CONTENT_SELECTOR = '[data-drawer-content]:not(a, button)';
const COMPONENT_BODY_TITLE_SELECTOR = '[data-drawer-title]:not(a, button)';
const COMPONENT_BODY_FOOTER_SELECTOR = '[data-drawer-footer]:not(a, button)';
const REMOTE_SHOW_ANIMATION = 'fadeIn';
export const COMPONENT_EXTERNAL_CONTENT_UPDATED_EVENT = 'mp-content-updated';

let componentInstanceId = 0;

/**
 * @typedef {{
 * rootElement?: string;
 * title?: string;
 * content?: string|Node|JQuery;
 * contentSelector?: string;
 * footer?: string;
 * url?: string;
 * method?: string;
 * data?: RequestDataInit;
 * dataFrom?: import('../../remote/partial-content.js').PartialDataFromInit;
 * relatedTarget?: HTMLElement;
 * scroll?: boolean;
 * backdrop?: boolean;
 * keyboard?: boolean;
 * focus?: boolean|string;
 * dismissible?: boolean;
 * dismissActions?: MpDataAction[];
 * loader?: boolean;
 * disposeOnHide?: boolean;
 * overlappable?: boolean;
 * delay?: number;
 * animated?: boolean;
 * position?: 'top'|'bottom'|'left'|'start'|'right'|'end';
 * size?: string;
 * width?: number|string;
 * height?: number|string;
 * wrapperClass?: string;
 * navigation?: boolean;
 * ajaxType?: string;
 * queueOnTransition?: boolean;
 * }} MpDrawerOptions
 *
 * @typedef {MpDrawerOptions & { skipDataset?: boolean }} MpDrawerStaticOptions
 */

/** @type {MpDrawerOptions} */
export const Default = {
	rootElement: 'body > div.drawer-container', // give the choice to place modal under different elements
	backdrop: true,
	keyboard: true,
	scroll: false,
	focus: true,
	dismissible: true,
	loader: true,
	disposeOnHide: true,
	overlappable: true,
	animated: true,
	position: 'end',
	size: '',
	navigation: false,
	ajaxType: 'drawer-content',
	queueOnTransition: true,
};

export const DefaultType = {
	backdrop: 'boolean',
	keyboard: 'boolean',
	scroll: 'boolean',
	focus: '(boolean|string)',
	closable: 'boolean', // deprecated
	dismissible: 'boolean',
	loader: 'boolean',
	disposeOnHide: 'boolean',
	overlappable: 'boolean',
	animated: 'boolean',
	position: 'string',
	size: 'string',
	navigation: 'boolean',
	// width: '(number|string)',
	// height: '(number|string)',
	// wrapperClass: 'string',
};

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

/**
 * Drawer component
 * @class
 */
export default class MpDrawer extends MpBaseComponent {

	// ==============
	// Props
	// ==============

	// * =====
	// * NOTE: Don't put any props here because this is an extended class and the super constructor will be called before anything else and it will break everything
	// * =====

	// =======================================================================================

	// ==============
	// Static Getters
	// ==============

	static get NAME() {
		return NAME;
	}

	static get DefaultType() {
		return DefaultType;
	}

	static get Default() {
		return Default;
	}

	// ==============
	// Static Methods
	// ==============

	/**
	 * @param {MpDrawerOptions} options
	 * @inheritdoc
	 */
	static buildTemplate(options) {

		const varClose = mpro.langvar('button_close');
		let {
			dismissible = true,
			wrapperClass,
			position,
			width,
			height,
			size,
			content,
			contentSelector,
		} = options;

		const defaultWrapperClass = ['offcanvas', COMPONENT_OVERLAY_CLASS];
		const wrapperAttr = ['aria-hidden="true"', 'tabindex="-1"', `data-overlay-type="${this.NAME}"`, `data-overlay-data-key="${this.DATA_KEY}"`, `data-overlay-event-key="${this.EVENT_KEY}"`];
		const wrapperStyle = [];
		const headerClass = [COMPONENT_HEADER_CLASS, COMPONENT_HEADER_EMPTY_CLASS];
		const titleAttr = [`class="${COMPONENT_TITLE_CLASS}"`, `id="offcanvas-title-${componentInstanceId}"`];
		const closeAttr = ['type="button"', 'class="btn-close"', 'data-bs-dismiss="offcanvas"', `aria-label="${varClose}"`, 'tabindex="-1"'];
		const defaultBodyClass = [COMPONENT_BODY_CLASS];
		const bodyAttr = ['data-mp-overlay-body'];
		const footerAttr = [`class=${COMPONENT_FOOTER_CLASS} data-mp-overlay-buttons`];

		// Add offcanvas position
		if (position) {
			defaultWrapperClass.push(`offcanvas-${position}`);
		}

		// Add extra wrapper class
		if (wrapperClass) {
			defaultWrapperClass.push(wrapperClass);
		}

		// Hide the close button
		if (dismissible === false) {
			closeAttr.push('style="display: none;"');
		}

		// Add related target aria attribute
		wrapperAttr.push(`aria-labelledby="offcanvas-title-${componentInstanceId}"`);

		// Drawer fixed width
		if (width) {
			wrapperStyle.push('width: ' + getCssSizeValue(width));
		}

		// Drawer fixed height
		if (height) {
			wrapperStyle.push('height: ' + getCssSizeValue(height));
		}

		// Dialog size
		if (size) {
			defaultWrapperClass.push('offcanvas-' + size);
		}

		// Add wrapper class attr
		if (defaultWrapperClass.length > 0) {
			wrapperAttr.push(`class="${defaultWrapperClass.join(' ')}"`);
		}

		// Add wrapper style attr
		if (wrapperStyle.length > 0) {
			wrapperAttr.push(`style="${wrapperStyle.join('; ')}"`);
		}

		// Load content from dom partial
		if (!content && contentSelector) {
			content = $(contentSelector).clone();
		}

		// Add body class attr
		if (defaultBodyClass.length > 0) {
			bodyAttr.push(`class="${defaultBodyClass.join(' ')}"`);
		}

		let nodeContent;
		if (content instanceof Node || content instanceof jQuery) {
			nodeContent = content;
			content = '';
		}

		// -----------------------------------------
		// Note: the options.title & options.footer will be provided inside the _handleContent() method
		// -----------------------------------------

		const $template = $(`<div ${wrapperAttr.join(' ')}>
			<div class="${COMPONENT_CONTENT_CLASS}">
				<div class="${headerClass.join(' ')}">
					<div ${titleAttr.join(' ')}></div>
					<button ${closeAttr.join(' ')}></button>
				</div>
				<div ${bodyAttr.join(' ')}>
					${content ?? ''}
				</div>
				<div ${footerAttr.join(' ')}></div>
			</div>
		</div>`);

		// Add the node content
		if (nodeContent) {
			// @ts-ignore
			$template.find('div[data-mp-overlay-body]').append(nodeContent);
		}

		return $template;
	}

	/**
	 * @param {MpDrawerStaticOptions} options
	 */
	static show(options = {}) {

		if (options.relatedTarget && !options.skipDataset) {
			options = { ...getOptionsFromDataset(options.relatedTarget, this.NAME, true), ...options };
		}

		const instance = new this(options);
		instance.show();
		return instance;
	}

	// ==============
	// Public Methods
	// ==============

	/**
	 * @inheritdoc
	 *
	 * @param {string|HTMLElement|MpDrawerOptions} elementOrConfig
	 * @param {MpDrawerOptions=} config
	 */
	init(elementOrConfig, config) {

		/** @type {MpDrawerOptions} */
		this._config = undefined;
		/** @type {number} */
		this._showTimer = null;
		/** @type {{ abort?: () => void }} */
		this._abortController = {};
		/** @type {boolean} */
		this._isRemoteContent = false;

		// Init base
		super.init(elementOrConfig, config);

		// Increment instance identifier
		componentInstanceId++;
		// Add the instance identifier
		this._instanceId = componentInstanceId;

		this._backdrop = this._initializeBackDrop();
		this._focustrap = this._initializeFocusTrap();
		this._isShown = false;
		this._isShowing = false;
		this._isHiding = false;
		this._addEventListeners();

		const domElement = this._element;

		// Add specific data identifier
		domElement.setAttribute(COMPONENT_INSTANCE_ATTRIBUTE, String(componentInstanceId));

		// Add element to DOM
		this._attachElement();

		// Make non-disposable
		if (this._canDetachElement === false) {
			this._config.disposeOnHide = false;
		}

		// Init plugins on the body of the component
		const $body = this.getBody$();
		if ($body.children().length > 0) {
			mpro.initStatics($body);
		}
	}

	/**
	 * @param {MpDrawerOptions} config
	 */
	_getConfig(config) {

		// TODO: this is not working here, needs to be fixed before calling buildTemplate()
		if (config.position) {
			if (config.position === 'left') {
				config.position = 'start';
			}
			else if (config.position === 'right') {
				config.position = 'end';
			}
		}

		// Normalize config
		config = super._getConfig(config);

		// Unclosable instance
		if (config.dismissible === false) {
			// No Escape key
			config.keyboard = false;
		}

		return config;
	}

	reloadContent() {
		this.getInner$()?.triggerNative(COMPONENT_RELOAD_EVENT);
	}

	getInner$() {
		return $(this._element).find(COMPONENT_INNER_CONTENT_SELECTOR);
	}

	getHeader$() {
		return $(this._element).find(COMPONENT_HEADER_SELECTOR);
	}

	getTitle$() {
		return this.getHeader$().find(COMPONENT_TITLE_SELECTOR);
	}

	getBody$() {
		return $(this._element).find(COMPONENT_BODY_SELECTOR);
	}

	getRemoteBody$() {
		return this.getBody$();
	}

	getFooter$() {
		return $(this._element).find(COMPONENT_FOOTER_SELECTOR);
	}

	toggle(options = {}) {
		return this._isShown ? this.hide() : this.show(options);
	}

	show(options = {}) {

		// TODO: decide if we should use reInit for this method only
		if (this._isDestroyed) {
			// @ts-ignore
			// console.warn('[%s] component instance is destroyed: %o', this.constructor.NAME, this);
			// return this;
			this.reInit();
		}

		if (options.relatedTarget) {
			this._config.relatedTarget = options.relatedTarget;
		}

		const {
			queueOnTransition,
			delay,
			overlappable,
			relatedTarget,
			scroll,
			animated,
		} = this._config;

		// Currently animating
		if (this._ac) {
			// Allow callback queueing ONLY when animations of the same type are running
			if (queueOnTransition && this._isShowing) {
				return this;
			}
			// End current transition
			this.endAnim();
			// Clear prev delay (need to call it here also)
			this._clearShowTimer();
		}

		// End backdrop animation
		this._backdrop.endAnim();

		// Component is already visible
		if (this._isShown) {
			return this;
		}

		if (!delay) {
			const showEvent = EventHandler.trigger(this._element, EVENT_SHOW, { relatedTarget });
			if (showEvent.defaultPrevented) {
				return this;
			}
		}

		// Clear prev delay
		this._clearShowTimer();

		// Add some delay (in seconds) before showing
		if ((delay > 0) && !options.force) {
			this._showTimer = window.setTimeout(() => {
				this._showTimer = null;
				this.show({ force: true });
			}, delay * 1000);
			return this;
		}

		// Close all other modals
		if (overlappable === false) {
			/** @type {typeof MpDrawer} */ // @ts-ignore
			const ctr = this.constructor;
			for (const instance of ctr.getAll({ dismissible: true, overlappable: false, visible: true })) {
				instance !== this && instance.hide();
			}
		}

		this._element.style.visibility = 'visible';
		this._backdrop.show();

		// Add a scrollbar helper
		if (!scroll) {
			new ScrollBarHelper().hide();
		}

		this._element.style.display = 'block';
		this._element.removeAttribute('aria-hidden');
		this._element.setAttribute('aria-modal', 'true');
		this._element.setAttribute('role', 'dialog');

		// Rearrange header/footer content
		this._handleContent();

		// Navigation handlers
		this._handleContentUpdate();

		// Find & load remote content
		this._handleRemoteContent();

		this._isShowing = true;

		// Trigger the 'pre-show' event
		// ? Note: this event is triggered after the backdrop is shown
		// ? Note: this event is necessary because the content is not yet visible and some components need to be initialized in this state
		// ? in order to prevent flickering
		EventHandler.trigger(this._element, EVENT_PRE_SHOW, { relatedTarget });

		// Start animation
		this._animateElement('add', animated).done(() => {
			this._handleContentIsInteractable();
			this._isShowing = false;
			this._isShown = true;

			// So we know the overlay is fully visible
			this._element.classList.add(COMPONENT_IS_VISIBLE_CLASS);

			EventHandler.trigger(this._element, EVENT_SHOWN, { relatedTarget });
		});

		return this;
	}

	/**
	 * Hide the modal instance
	 *
	 * @param {boolean} forceNoTransition
	 */
	hide(forceNoTransition = false) {

		if (this._isDestroyed) {
			// @ts-ignore
			console.warn('[%s] component instance is destroyed: %o', this.constructor.NAME, this);
			return this;
		}

		// Currently animating
		if (this._ac) {
			// Allow callback queueing ONLY when animations of the same type are running AND the instant hide is NOT enabled
			if (this._config.queueOnTransition && this._isHiding && (forceNoTransition !== true)) {
				return this;
			}
			// End current transition
			this.endAnim();
			// Clear prev delay (need to call it here also)
			this._clearShowTimer();
		}

		// End backdrop animation
		this._backdrop.endAnim();

		if (!this._isShown) {
			return this;
		}

		const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE);

		if (hideEvent.defaultPrevented) {
			return this;
		}

		// Clear prev delay
		this._clearShowTimer();

		this._focustrap.deactivate();
		this._element.blur();
		this._backdrop.hide();

		// So we know the overlay is not visible anymore
		this._element.classList.remove(COMPONENT_IS_VISIBLE_CLASS);

		// Execute the dismiss action
		if (this._config.dismissActions) {
			this._handleDismissActions(this._config.dismissActions);
		}

		const isAnimated = (forceNoTransition === true) ? false : this._config.animated;

		this._isHiding = true;

		this._animateElement('remove', isAnimated).done(() => {
			this._element.setAttribute('aria-hidden', 'true');
			this._element.removeAttribute('aria-modal');
			this._element.removeAttribute('role');
			this._element.style.visibility = 'hidden';

			if (!this._config.scroll) {
				new ScrollBarHelper().reset();
			}

			this._isHiding = false;
			this._isShown = false;

			EventHandler.trigger(this._element, EVENT_HIDDEN);

			// Dispose on hide
			if (this._config.disposeOnHide) {
				this.dispose();
			}
			// Discard component contents if loaded from remote
			else if (!this._isDestroyed && (this._isRemoteContent === true)) {
				this._abortRemote();
				this.getTitle$().empty();
				this.getHeader$().addClass(COMPONENT_HEADER_EMPTY_CLASS);
				this.getBody$().empty();
				this.getFooter$().empty();
			}
		});

		return this;
	}

	/**
	 * Close & dispose the modal instance
	 *
	 * @param {boolean} forceNoTransition
	 */
	close(forceNoTransition = true) {
		this.hide(forceNoTransition).getAnim().done(_ => this.dispose());
		return this;
	}

	dispose() {
		// End current transition
		this.endAnim();
		// Clear prev delay (need to call it here also)
		this._clearShowTimer();
		// Abort pending network requests
		this._abortRemote();

		// Already disposed
		// ! Note: This MUST be called after endAnim() method because if will also call dispose() and it might skip the _isDestroyed check
		if (this._isDestroyed) {
			return;
		}

		// Get the jQuery instance of the remote container
		this.getRemoteBody$()?.remove();
		// Remove listeners and make sure all jQuery data is also removed
		this.getInner$()?.off(COMPONENT_RELOAD_EVENT).remove();

		this._backdrop.dispose();
		this._focustrap.deactivate();
		super.dispose();
	}

	// ==========================
	// Private/Protected Methods
	// ==========================

	_clearShowTimer() {
		if (this._showTimer) {
			window.clearTimeout(this._showTimer);
			this._showTimer = null;
		}
	}

	/**
	 * Animation shortcut
	 *
	 * @param {'add'|'remove'} mode
	 * @param {boolean} isAnimated
	 */
	_animateElement(mode, isAnimated) {
		return this.startAnim(this._element, [CLASS_NAME_SHOW], mode, isAnimated);
	}

	/**
	 * Abort ongoing network requests
	 */
	_abortRemote() {
		if (this._abortController?.abort) {
			this._abortController.abort();
		}
	}

	/**
	 * Select modal title/footer from the main modal content and place it inside their respective containers
	 *
	 * @param {any} [extra={}]
	 */
	_handleContent(extra = {}) {

		const config = { ...this._config, ...extra };
		const $componentBody = this.getBody$();
		const $titleElem = this.getTitle$();
		const $footerElem = this.getFooter$();
		let hasTitle = Boolean(config.title);
		let hasFooter = Boolean(config.footer);

		// Remove elements that are not needed in overlays
		$componentBody.find('[data-remove-in-overlay]').remove();

		// Process title from the body
		const $bodyTitle = $componentBody.find($componentBody.find('[data-drawer-title-selector]').data('drawer-title-selector') || COMPONENT_BODY_TITLE_SELECTOR);

		// Place the title from the body contents in the header
		if ($bodyTitle.length > 0) {
			// Empty the previous title
			$titleElem.empty();
			$titleElem.append($bodyTitle);
			hasTitle = true;
		}
		// Add the provided title from options
		else if (hasTitle) {
			$titleElem.html(config.title);
		}
		// Remove previous title
		else {
			$titleElem.empty();
		}

		// Make sure the header toggles a specific class when the title is empty
		this.getHeader$().toggleClass(COMPONENT_HEADER_EMPTY_CLASS, !hasTitle);

		// Process footer from the body
		const $bodyFooter = $componentBody.find($componentBody.find('[data-drawer-footer-selector]').data('drawer-footer-selector') || COMPONENT_BODY_FOOTER_SELECTOR);

		// Empty the previous footer
		$footerElem.empty();

		// Place the footer from the body contents in the footer
		if ($bodyFooter.length > 0) {

			// Link submit buttons to original form
			/** @type {JQuery<HTMLButtonElement>} */ // @ts-ignore
			const $formTriggerButtons = $bodyFooter.find('button:not([type="button"],[type="reset"])');

			if ($formTriggerButtons.length > 0) {
				$formTriggerButtons.each(function() {
					// Only process buttons that have a form and the form is not already inside the footer
					if (this.form && $(this.form).closest($bodyFooter).length === 0) {
						// Save the form reference
						/** @type {JQuery<HTMLFormElement>} */ // @ts-ignore
						const $form = $(this.form);
						const $button = $(this);

						// Clone the button
						const $clone = $button.clone().removeAttr('id').removeAttr('name').insertAfter($button);
						$button.appendTo($form).hide();

						// Trigger the form submit/reset event
						$clone.on('click.forward-form-action', (e) => {
							e.preventDefault();

							// TODO: forward the loading animation to the handler

							// Click the original
							$button[0].click();
						});
					}
				});
			}

			$footerElem.append($bodyFooter);
			hasFooter = true;
		}
		// Add the provided footer from options
		else if (hasFooter) {
			$footerElem.html(config.footer);
		}
	}

	_handleContentUpdate() {

		// Resolve lazy template content
		mpro.resolveLazyContent(this.getBody$());

		// Allow component url navigation
		if (this._config.navigation) {

			// eslint-disable-next-line consistent-this
			const drawer = this;
			const $body = this.getBody$();
			const { click_selector, submit_selector } = mpro.config.rules.overlays.navigation;

			// Select content loaders
			/** @type JQuery<HTMLAnchorElement | HTMLAreaElement> */ // @ts-ignore
			const $links = $body.find(click_selector);
			/** @type JQuery<HTMLFormElement> */ // @ts-ignore
			const $forms = $body.find(submit_selector);

			// Link click
			$links.off('click.overlay-navigation').on('click.overlay-navigation', function(e) {

				// Continue as normal for cmd clicks etc
				if (e.which > 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
					return void 0;
				}

				const $link = $(this);

				// Trigger load
				drawer._loadRemoteContent({
					url: $link.attr('href'),
					method: 'GET',
					loader: drawer._config.loader,
				})
					.then(() => {
						// Reset the scroll position
						$body.scrollTop(0);
					});

				// Stop all propagation
				return false;
			});

			// Form submit
			$forms.off('submit.overlay-navigation').on('submit.overlay-navigation', function(e) {

				const $form = $(this);
				/** @type {SubmitEvent} */ // @ts-ignore
				const origEvt = e.originalEvent;
				const { submitter } = origEvt;
				const formData = new FormData(this, submitter);
				const submitterName = submitter.getAttribute('name');
				// Add the submitter to the form data
				if (submitterName && !formData.has(submitterName)) {
					formData.set(submitterName, submitter.getAttribute('value'));
				}

				// Trigger load
				drawer._loadRemoteContent({
					url: $form.attr('action') || document.URL,
					method: $form.attr('method') || 'POST',
					// @ts-ignore
					data: (this.enctype === 'multipart/form-data') ? formData : new URLSearchParams(formData),
					// Dirty approach to find the main submit button ([data-no-loader] is added to search widgets with mini-submit buttons)
					loader: ($form.find('button:not([type="button"],[type="reset"]):visible').not('[data-no-loader]').length > 0) ? false : drawer._config.loader,
				})
					.then(() => {
						// Reset the scroll position
						$body.scrollTop(0);
					});

				// Stop all propagation
				return false;
			});
		}
	}

	_handleContentIsInteractable() {

		// Autofocus
		if (!this._config.scroll || this._config.focus) {
			this._focustrap.activate();
		}
	}

	/**
	 * Remote content handler (loading & navigation)
	 */
	async _handleRemoteContent() {

		// Load remote content
		if (this._config.url) {

			const NS_RELOAD_EVENT = COMPONENT_RELOAD_EVENT + '.reload-overlay-content';
			const remoteOptions = {};
			({
				url: remoteOptions.url,
				method: remoteOptions.method,
				data: remoteOptions.data,
				dataFrom: remoteOptions.dataFrom,
				loader: remoteOptions.loader,
			} = this._config);

			// Add event listener for content reload requests
			this.getInner$().off(NS_RELOAD_EVENT).on(NS_RELOAD_EVENT, (e) => {
				e.stopPropagation();
				e.stopImmediatePropagation();

				// Reload the content
				this._loadRemoteContent(remoteOptions);
			});

			// Wait for content to load before returning
			await this._loadRemoteContent(remoteOptions);
		}
		// No remote content
		else {
			// Trigger the 'contentLoaded' event
			EventHandler.trigger(this._element, EVENT_CONTENT_LOADED);
		}

		// Attach event listener for external content update
		$(this._element).off(COMPONENT_EXTERNAL_CONTENT_UPDATED_EVENT).on(COMPONENT_EXTERNAL_CONTENT_UPDATED_EVENT, (e, extra) => {
			e.stopPropagation();
			e.stopImmediatePropagation();

			extra = extra || {};
			// Must make sure to provide a title & footer, otherwise _handleContent() will remove them
			const extraContent = {};
			extraContent.title = extra.title ?? this.getTitle$().html();
			extraContent.footer = extra.footer ?? this.getFooter$().html();

			// Rearrange header/footer content
			this._handleContent(extraContent);
			// Content processing logic after content is updated
			this._handleContentUpdate();
			this._handleContentIsInteractable();

			// Trigger the 'contentLoaded' event
			EventHandler.trigger(this._element, EVENT_CONTENT_LOADED);
		});
	}

	/**
	 * @param {import('../../remote/partial-content').LoadPartialOptions=} loadPartialOptions
	 */
	async _loadRemoteContent(loadPartialOptions = {}) {

		const $innerContent = this.getInner$();
		const $remoteContainer = this.getRemoteBody$();

		let partial;

		try {
			// The body now/will contain(s) remote content
			this._isRemoteContent = true;
			// Reload partial content from remote source
			partial = await loadPartial($remoteContainer, {
				...loadPartialOptions,
				type: this._config.ajaxType,
				contentSelector: COMPONENT_CONTENT_SELECTOR,
				emptyContainer: true,
				animation: false,
				ref: this._abortController,
				relatedTarget: this._config.relatedTarget,
			});
		}
		catch (/** @type {any} */ error) {
			if (/AbortError/.test(error?.message ?? '')) {
				return;
			}
		}
		finally {
			// Clear the abort ref (instance might already be destroyed)
			if (this._abortController) {
				this._abortController = {};
			}
		}

		// Instance is already destroyed
		if (this._isDestroyed) {
			return;
		}

		// Some response keys should trigger a modal close
		if (partial.isContentful() === false) {
			// Close the instance
			this.hide(); // todo: maybe use this.close() when partial.location is set
			// Handle extra response fields using the standard handler
			try {
				return void handleRemoteResponse(partial);
			}
			catch (error) {
				console.warn('Error handling remote response: %o', error);
			}
		}

		// Handle extra response fields using the standard handler
		try {
			handleRemoteResponse(partial);
		}
		catch (error) {
			console.warn('Error handling remote response: %o', error);
		}

		// Add extra content from the response
		const extra = {};

		// Add the title from the response only if it's not already set
		if (partial.title/*  && !this._config.title */) {
			extra.title = partial.title;
		}

		// Add the footer from the response only if it's not already set
		if (partial.footer/*  && !this._config.footer */) {
			extra.footer = partial.footer;
		}

		// Animate new content
		cssAnimation($innerContent[0], REMOTE_SHOW_ANIMATION, 'faster');
		// cssAnimation(this._body, REMOTE_SHOW_ANIMATION, 'slow');
		// cssAnimation(this._footer, REMOTE_SHOW_ANIMATION, 'slow');

		// Rearrange header/footer content
		this._handleContent(extra);
		// Content processing logic after content is updated
		this._handleContentUpdate();
		this._handleContentIsInteractable();

		// Trigger the 'contentLoaded' event
		EventHandler.trigger(this._element, EVENT_CONTENT_LOADED);
	}

	_initializeBackDrop() {
		return new Backdrop({
			className: CLASS_NAME_BACKDROP,
			isVisible: this._config.backdrop,
			isAnimated: this._config.animated,
			// rootElement: this._element.parentNode,
			clickCallback: () => void (this._config.dismissible && this.hide()),
		});
	}

	_initializeFocusTrap() {
		return new FocusTrap({
			trapElement: this._element,
			autofocus: this._config.focus,
		});
	}

	_addEventListeners() {
		EventHandler.on(this._element, EVENT_KEYDOWN_DISMISS, (event) => {
			if (this._config.keyboard && event.key === ESCAPE_KEY) {
				this.hide();
			}
		});
	}
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, /** @this {HTMLElement} */ function(event) {
	const target = getElementFromSelector(this);

	if (['A', 'AREA'].includes(this.tagName)) {
		event.preventDefault();
	}

	if (isDisabled(this)) {
		return;
	}

	EventHandler.one(target, EVENT_HIDDEN, () => {
		// focus on trigger when it is closed
		if (isVisible(this)) {
			this.focus();
		}
	});

	// avoid conflict when clicking a toggler of an offcanvas, while another is open
	/** @type {HTMLElement} */
	// @ts-ignore
	const alreadyOpen = SelectorEngine.findOne(OPEN_SELECTOR);
	if (alreadyOpen && alreadyOpen !== target) {
		MpDrawer.getInstance(alreadyOpen).hide();
	}

	const data = MpDrawer.getOrCreateInstance(target);
	data.toggle(this);
});

/**
 * Open instance already present on load
 * @deprecated This doesn't work in all cases, use the jQuery ready handler instead
 */
// EventHandler.on(window, EVENT_LOAD_DATA_API, () => {
// 	for (const el of SelectorEngine.find(AUTO_OPEN_SELECTOR)) {
// 		MpDrawer.getOrCreateInstance(el).show();
// 	}
// });

/**
 * Global dismiss trigger
 */
// enableDismissTrigger(MpDrawer);

// Add init function that is executed only on first dom-ready (it should contain only document/body delegate handlers)
mpro.addInitDelegate(() => {

	// Open instance already present on load
	for (const el of SelectorEngine.find(AUTO_OPEN_SELECTOR)) {
		MpDrawer.getOrCreateInstance(el).show();
	}

	// Delegate handler to enable closing instances
	MpDrawer.enableDismissTrigger();
});

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */
MpDrawer.defineJQueryPlugin();
